import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState } from "@redotech/react-util/load";
import { Tag } from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { sortDefault } from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { viewTypes } from "@redotech/redo-views-common/views";
import { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { CheckboxValue } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Flex } from "@redotech/redo-web/flex";
import { PageMainContentRefContext } from "@redotech/redo-web/page";
import {
  RowClickHandler,
  TableFetcher,
  TableRef,
} from "@redotech/redo-web/table";
import {
  DefaultEmptyContentMessage,
  DefaultLoadingContentMessage,
  DisplayedColumn,
  SaveViewBody,
  Table2,
} from "@redotech/redo-web/table2";
import classNames from "classnames";
import { memo, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { EnabledFulfillmentProvidersContext } from "../common/enabled-fulfillment-providers";
import { getExpandedItems } from "../utils/outbound-labels-utils";
import { ExportOrdersModal } from "./export-orders-modal";
import { getFGAdvancedFilters } from "./filters/get-fg-advanced-filters";
import { getFulfillmentOrderColumns } from "./fulfillment-group-columns";
import { getFulfillmentOrderCsv } from "./fulfillment-group-csv";
import { FulfillmentGroupOrderActions } from "./fulfillment-group-order-actions";
import { HideSlideoutCheckbox } from "./hide-slideout-checkbox";
import { useOrderNavigation } from "./order-navigation-context";
import * as ordersCss from "./outbound-labels.module.css";

interface FulfillmentOrderTableProps {
  allChecked: boolean;
  someChecked: boolean;
  fetcher: TableFetcher<FulfillmentGroupData>;
  handleRowClick: RowClickHandler<FulfillmentGroupData>;
  pageNumber: number;
  selectedRows: ReadonlySet<string>;
  clearSelection: () => void;
  setPageNumber: (pageNumber: number) => void;
  tableRef: React.RefObject<TableRef<FulfillmentGroupData>>;
  availableTagsLoad: LoadState<Tag[] | undefined>;
  getAvailableTags: (signal?: AbortSignal) => void;
  toggleAllSelection: (selected: boolean) => void;
  handleCheckClick: (id: string, e: React.MouseEvent<HTMLDivElement>) => void;
  checkedSubItems: Map<string, Set<number>>;
  getRowCheckboxState: (rowId: string) => CheckboxValue;
  toggleSubItemSelection: (rowId: string, subItemIndex: number) => void;
  refreshSymbol: symbol;
  searchRefreshSymbol: symbol;
  updateRowData: (rowUpdates: FulfillmentGroupUpdate[]) => void;
  saveView: ({
    name,
    filters,
    columns,
    sort,
    pageSize,
    position,
  }: SaveViewBody) => void;
  saveViewLoad: LoadState<void>;
  rpcClient: RedoOutboundLabelsRpcClient;
  setViewBeingEdited?: (viewBeingEdited: boolean) => void;
  viewBeingEdited?: boolean;
  activeView: viewTypes["fulfillment-groups"];
  reservedViewNames: Set<string>;
  onDeleteView: (view: viewTypes["fulfillment-groups"]) => void;
  slideoutOpen: boolean;
  users: OMSUser[];
  pageSize: number;
  setPageSize: (size: number) => void;
  setMergeModalOpen: (open: boolean) => void;
  showMergeButton: boolean;
  mergeDisabledReason: string;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  groupsWaitingForSplit?: string[];
  setGroupsWaitingForReplacement: (groups: string[]) => void;
  hideSlideout: boolean;
  setHideSlideout: (show: boolean) => void;
}

const FulfillmentOrderTable_ = function FulfillmentOrderTable({
  allChecked,
  someChecked,
  fetcher,
  pageNumber,
  selectedRows,
  clearSelection,
  setPageNumber,
  tableRef,
  availableTagsLoad,
  getAvailableTags,
  toggleAllSelection,
  handleCheckClick,
  handleRowClick,
  checkedSubItems,
  getRowCheckboxState,
  toggleSubItemSelection,
  refreshSymbol,
  saveView,
  saveViewLoad,
  rpcClient,
  updateRowData,
  viewBeingEdited,
  setViewBeingEdited,
  activeView,
  reservedViewNames,
  onDeleteView,
  slideoutOpen,
  users,
  pageSize,
  searchRefreshSymbol,
  setPageSize,
  setMergeModalOpen,
  showMergeButton,
  mergeDisabledReason,
  handleUpdates,
  groupsWaitingForSplit = [],
  setGroupsWaitingForReplacement,
  hideSlideout,
  setHideSlideout,
}: FulfillmentOrderTableProps): React.JSX.Element {
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [currentViewForExport, setCurrentViewForExport] =
    useState<viewTypes["fulfillment-groups"]>(activeView);

  const pageMainContentRef = useRequiredContext(PageMainContentRefContext);

  const enabledProviders = useRequiredContext(
    EnabledFulfillmentProvidersContext,
  );
  const advancedFilters = useMemo(
    (): AdvancedTableFilter[] =>
      getFGAdvancedFilters({
        team,
        users,
        rpcClient,
        rpcMerchantClient,
        enabledProviders,
      }),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const cols = getFulfillmentOrderColumns({
    allChecked,
    someChecked,
    toggleAllSelection,
    handleCheckClick,
    checkedSubItems,
    getRowCheckboxState,
    toggleSubItemSelection,
    availableTagsLoad,
    getAvailableTags,
    updateRowData,
    autoCleanAddresses:
      team?.settings?.outboundLabels?.address?.autoCleanAddresses ?? false,
    weightFormat:
      team?.settings?.outboundLabels?.weightFormat ?? WeightFormat.IMPERIAL,
    groupsWaitingForSplit,
    customMetadataSettings: team?.settings?.outboundLabels?.customMetadata,
    customAttributesSettings: team?.settings?.outboundLabels?.customAttributes,
    productCustomMetadataSettings:
      team?.settings?.outboundLabels?.productCustomMetadata,
    variantCustomMetadataSettings:
      team?.settings?.outboundLabels?.variantCustomMetadata,
    teamProviders: enabledProviders,
    teamType: team?.teamType,
    wmsEnabled: team?.settings?.outboundLabels?.wms?.enabled ?? false,
    hideInputItems:
      team?.settings?.outboundLabels?.billOfMaterials?.alwaysHideInputItems ??
      false,
    users,
  });

  const viewWithAllCols: viewTypes["fulfillment-groups"] = useMemo(() => {
    const view = activeView;

    for (const col of cols) {
      const viewCol = activeView.columns.find((c) => c.key === col.key);

      if (!viewCol) {
        view.columns.push({ key: col.key, hidden: true, width: col.width });
      }
    }
    return view;
  }, [activeView, cols]);

  const getCsvColumns = useHandler((displayedColumns: DisplayedColumn[]) => {
    return getFulfillmentOrderCsv({
      wmsEnabled: team?.settings?.outboundLabels?.wms?.enabled ?? false,
      autoCleanAddresses:
        team?.settings?.outboundLabels?.address?.autoCleanAddresses ?? false,
      weightFormat:
        team?.settings?.outboundLabels?.weightFormat ?? WeightFormat.IMPERIAL,
      columns: viewWithAllCols.columns,
      displayedColumns,
      customMetadataSettings: team?.settings?.outboundLabels?.customMetadata,
      customAttributesSettings:
        team?.settings?.outboundLabels?.customAttributes,
      productCustomMetadataSettings:
        team?.settings?.outboundLabels?.productCustomMetadata,
      variantCustomMetadataSettings:
        team?.settings?.outboundLabels?.variantCustomMetadata,
    });
  });

  const selectedFulfillmentGroups = useMemo(
    () => fetcher.rows?.filter((row) => selectedRows.has(row._id)) || [],
    [fetcher.rows, selectedRows],
  );

  const { initialize } = useOrderNavigation();
  const location = useLocation();

  const initializeGroupNavigation = useHandler(
    async (fulfillmentGroups: FulfillmentGroupData[]) => {
      initialize(
        fulfillmentGroups.map((group) => {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          const isRedoOrder = !!group.orders[0].redoOrderId;
          const orderId = isRedoOrder
            ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              group.orders[0].redoOrderId
            : // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              group.orders[0].id;
          return { id: group._id, orderId: orderId ?? "", isRedoOrder };
        }),
        location.pathname + location.search + location.hash,
      );
    },
  );

  const aboveTableContent = (
    <Flex gap="lg" pl="md">
      <HideSlideoutCheckbox
        hideSlideout={hideSlideout}
        setHideSlideout={setHideSlideout}
      />
      <FulfillmentGroupOrderActions
        clearSelection={clearSelection}
        fulfillmentGroups={selectedFulfillmentGroups}
        handleUpdates={handleUpdates}
        mergeDisabledReason={mergeDisabledReason}
        selectedRows={selectedRows}
        setGroupsWaitingForReplacement={setGroupsWaitingForReplacement}
        setMergeModalOpen={setMergeModalOpen}
        showMergeButton={showMergeButton}
        users={users}
      />
    </Flex>
  );

  const handleExportRequested = useHandler(
    (view: viewTypes["fulfillment-groups"]) => {
      setCurrentViewForExport(view);
      setExportModalOpen(true);
    },
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
        return;
      }

      const rows = fetcher.rows;
      if (!rows || rows.length === 0) {
        return;
      }

      // Disable keyboard shortcuts when multiple rows are selected
      if (selectedRows.size !== 1) {
        return;
      }

      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      e.preventDefault();

      const selectedId = Array.from(selectedRows)[0];
      const currentIndex = rows.findIndex((row) => row._id === selectedId);

      if (currentIndex < 0) {
        return;
      }

      let nextIndex: number;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex + 1;
        if (nextIndex >= rows.length) {
          nextIndex = rows.length - 1;
        }
      } else {
        nextIndex = currentIndex - 1;
        if (nextIndex < 0) {
          nextIndex = 0;
        }
      }

      if (nextIndex >= 0 && nextIndex < rows.length) {
        const nextRow = rows[nextIndex];
        const syntheticEvent = {
          currentTarget: null,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as unknown as React.MouseEvent<HTMLTableRowElement>;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        handleRowClick(nextRow, syntheticEvent, nextIndex);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [fetcher.rows, selectedRows, handleRowClick]);

  return (
    <div
      className={classNames(ordersCss.tableContainer)}
      onMouseDown={(e) => e.shiftKey && e.preventDefault()}
    >
      <Table2
        aboveTableContent={aboveTableContent}
        activeView={viewWithAllCols}
        adjustableColumnWidths
        advancedFilters={advancedFilters}
        allowPinning
        alwaysLoadTable
        columns={cols}
        columnSelection
        EmptyContentMessage={<DefaultEmptyContentMessage borderless />}
        enableColumnReordering
        enableFilterGroups
        expandAllStorageKey={`fulfillment-group-table:expand-all:${team._id}`}
        expander={({ row }) => {
          const expandedItems = getExpandedItems(row);
          return {
            numExpansions: expandedItems.length,
            expandable: expandedItems.length > 1,
          };
        }}
        fetcher={fetcher}
        focusSearchOnRender
        getCsvColumns={getCsvColumns}
        isRowSelected={(item: FulfillmentGroupData) =>
          selectedRows.has(item._id)
        }
        LoadingContentMessage={<DefaultLoadingContentMessage borderless />}
        nestedFilterCascade
        onDeleteView={onDeleteView}
        onExportRequested={handleExportRequested}
        onRowClick={handleRowClick}
        pageNumber={pageNumber}
        pageSize={pageSize}
        ref={tableRef}
        refreshSymbol={refreshSymbol}
        reservedViewNames={reservedViewNames}
        saveView={saveView}
        saveViewLoad={saveViewLoad}
        scrollAreaRef={pageMainContentRef}
        searchEnabled
        searchRefreshSymbol={searchRefreshSymbol}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        setViewBeingEdited={setViewBeingEdited}
        size="sm"
        sortDefault={sortDefault}
        stickyPageControl
        surfaceData={initializeGroupNavigation}
        tableContentClassName={classNames(ordersCss.fulfillmentGroupTable, {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [ordersCss.narrow]: slideoutOpen && !hideSlideout,
        })}
        toolbarEndPaddingRight={slideoutOpen && !hideSlideout ? 378 : undefined}
        useVirtualTable
        viewBeingEdited={viewBeingEdited}
      />
      <ExportOrdersModal
        currentView={currentViewForExport}
        onClose={() => setExportModalOpen(false)}
        open={exportModalOpen}
        rpcClient={rpcClient}
        selectedFulfillmentGroups={selectedFulfillmentGroups}
      />
    </div>
  );
};

export const FulfillmentOrderTable: React.NamedExoticComponent<FulfillmentOrderTableProps> =
  memo(FulfillmentOrderTable_);
